import React, { useEffect, useCallback, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Box, Typography, CircularProgress, Button } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import EditIcon from '@material-ui/icons/Edit';

import { PermissionsProvider } from 'providers/permissionsProvider';
import {
  CAN_EDIT_MODEL,
  CAN_EDIT_SP_POTENTIAL_MATCHES,
  CAN_SEE_SP_POTENTIAL_MATCHES,
} from 'providers/permissions';
import { useVersion } from 'react-admin';
import { SnackbarContainer } from 'components/standalone';

import { ModelContainer } from '../containers/useModelContainer';
import { SpMatchesSection } from '../components/SpMatchesSection';
import { ModelForm } from '../components/ModelForm';

const useStyles = makeStyles(theme => ({
  root: {
    maxWidth: '1500px',
  },
  topToolbar: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
    minHeight: 'auto',
  },
  formContainer: {
    position: 'relative',
    boxShadow: theme.shadows[2],
    backgroundColor: theme.palette.background.paper,
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(8),
  },
}));

interface RouteParams {
  id: string;
}

const ModelShowInner: React.FC = () => {
  const classes = useStyles();
  const history = useHistory();
  const { notify } = SnackbarContainer.useContainer();
  const { id } = useParams<RouteParams>();
  const { hasPermission } = PermissionsProvider.useContainer();

  const canEdit = hasPermission(CAN_EDIT_MODEL);
  const canSeeSpMatches = hasPermission(CAN_SEE_SP_POTENTIAL_MATCHES);
  const canEditSpMatches = hasPermission(CAN_EDIT_SP_POTENTIAL_MATCHES);

  const {
    selectedModel,
    isLoading,
    modelLoaded,
    modelError,
    loadModelById,
    loadSpModels,
    getBuilderForSubdivision,
  } = ModelContainer.useContainer();

  // BACKWARDS COMPAT: useVersion triggers re-fetch when react-admin
  // toolbar refresh button is clicked. Required while using custom
  // containers instead of react-admin's built-in data hooks.
  const version = useVersion();

  const [builderName, setBuilderName] = useState('');
  const [builderLoading, setBuilderLoading] = useState(false);

  const modelId = selectedModel?.id;
  const modelSubdivisionId = selectedModel?.subdivisionId;

  useEffect(() => {
    if (!id) return;
    loadModelById(id);
  }, [id, version, loadModelById]);

  useEffect(() => {
    if (modelError || (modelLoaded && !selectedModel)) {
      notify('Element does not exist', 'error');
      history.push('/model');
    }
  }, [modelError, modelLoaded, selectedModel, notify, history]);

  // Load SP models when model id changes
  useEffect(() => {
    if (!modelId || !canSeeSpMatches) return;
    loadSpModels(modelId);
  }, [modelId, canSeeSpMatches, loadSpModels]);

  // Load builder name when subdivision id changes
  useEffect(() => {
    if (!modelSubdivisionId) return;
    const loadBuilder = async () => {
      setBuilderLoading(true);
      const builder = await getBuilderForSubdivision(modelSubdivisionId);
      if (builder) {
        setBuilderName(builder.name);
      }
      setBuilderLoading(false);
    };
    loadBuilder();
  }, [modelSubdivisionId, getBuilderForSubdivision]);

  const handleEdit = useCallback(() => {
    history.push(`/model/${id}`);
  }, [history, id]);

  const handleGoBack = useCallback(() => {
    history.push('/model');
  }, [history]);

  if (isLoading) {
    return (
      <Box className={classes.loadingContainer}>
        <CircularProgress />
      </Box>
    );
  }

  if (!selectedModel) {
    return (
      <Box className={classes.root}>
        <Typography>Model not found</Typography>
      </Box>
    );
  }

  return (
    <Box className={classes.root}>
      <Box className={classes.topToolbar}>
        {canEdit && (
          <Button
            color="primary"
            startIcon={<EditIcon />}
            onClick={handleEdit}
            size="small"
          >
            Edit
          </Button>
        )}
      </Box>

      <Box className={classes.formContainer}>
        <ModelForm
          mode="show"
          initialValues={selectedModel}
          builderName={builderName}
          builderLoading={builderLoading}
          onGoBack={handleGoBack}
          loading={false}
        />
      </Box>

      {canSeeSpMatches && <SpMatchesSection canEdit={canEditSpMatches} />}
    </Box>
  );
};

export const ModelShow: React.FC = () => {
  return (
    <ModelContainer.Provider>
      <ModelShowInner />
    </ModelContainer.Provider>
  );
};

export default ModelShow;
